import React from "react";
import { useTranslation } from "react-i18next";
import { ParseKeys } from "i18next";
import { Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

import TextField from "components/basic/textField";
import AutoComplete from "components/basic/autoComplete";
import Button from "components/basic/button";

import { DateFilter } from "../../types";

const DATE_OPTIONS: {
  id: number;
  label: ParseKeys<"meetings">;
  value: DateFilter;
}[] = [
  { id: 1, label: "toolbar.all_dates", value: "all" },
  { id: 2, label: "toolbar.upcoming", value: "upcoming" },
  { id: 3, label: "toolbar.past", value: "past" },
  { id: 4, label: "toolbar.today", value: "today" },
  { id: 5, label: "toolbar.week", value: "week" },
  { id: 6, label: "toolbar.month", value: "month" },
];

interface MeetingsToolbarProps {
  search: string;
  dateFilter: DateFilter;
  onSearch: (value: string) => void;
  onDateFilter: (value: DateFilter) => void;
  /** Opens the filter rail. Only rendered below md, where it is a drawer. */
  onOpenFilters: () => void;
}

const MeetingsToolbar: React.FC<MeetingsToolbarProps> = ({
  search,
  dateFilter,
  onSearch,
  onDateFilter,
  onOpenFilters,
}) => {
  const { t } = useTranslation("meetings");

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        mb: 1.5,
      }}
    >
      <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
        <TextField
          name="meeting-search"
          value={search}
          placeHolder={t("toolbar.search")}
          onChange={(e) => onSearch(e.target.value)}
          startAdornment={<SearchIcon fontSize="small" color="action" />}
        />
      </Box>

      <Box sx={{ width: { xs: "100%", sm: 180 } }}>
        <AutoComplete
          options={DATE_OPTIONS.map((o) => ({ id: o.id, label: t(o.label) }))}
          value={DATE_OPTIONS.find((o) => o.value === dateFilter)?.id ?? 1}
          onChange={(v) => {
            const opt = DATE_OPTIONS.find((o) => o.id === v?.id);
            onDateFilter(opt?.value ?? "all");
          }}
        />
      </Box>

      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Button
          variant="outlined"
          color="primary"
          startAdornment={<FilterListIcon fontSize="small" />}
          onClick={onOpenFilters}
        >
          {t("toolbar.filters")}
        </Button>
      </Box>
    </Box>
  );
};

export default MeetingsToolbar;
